import { useEffect, useState } from "react";
import dayjs from "dayjs";
import { toast } from "sonner";
import { api, apiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { canEdit } from "../../lib/permissions";
import { usePrograms } from "../../lib/usePrograms";
import { Card, Btn, Empty } from "../../components/UI";
import { NativeLikeSelect } from "../../components/CocoaSelect";
import { SlidePanel, ConfirmDialog } from "../../components/SlidePanel";
import { SignaturePromptModal } from "../../components/SignaturePromptModal";
import { FLabel, FInput, SelectWithCustom } from "../engagement/FormControls";
import { DateField } from "../engagement/DateField";
import ClinicalRecordLockControls from "../../components/ClinicalRecordLockControls";
import { VersionAccordion } from "../../components/VersionAccordion";
import { Plus, Filter, Check, Eye, Pencil, Trash2 } from "lucide-react";

const CN_COLS = 9;

const CATEGORY = "Medical";
const NOTE_TYPES = [
  "Medical Follow-Up", "Medication Review", "Vitals Check", "Lab Review",
  "Physical Exam", "Nursing Note", "Nursing Assessment", "Consultation Note",
  "Referral Note", "Discharge Summary",
];
const NOTE_EMPTY = {
  date: dayjs().format("YYYY-MM-DD"), time: "09:00", note_type: "",
  provider: "", program: "", category: CATEGORY, custom_notes: "",
};

const CHART_NOTE_AMEND_FIELDS = [
  { key: "note_type", label: "Note Type" },
  { key: "date", label: "Date" },
  { key: "time", label: "Time" },
  { key: "provider", label: "Provider" },
  { key: "program", label: "Program" },
];

export default function MedicalNotes({ client }) {
  const { user } = useAuth();
  const editable = canEdit(user, "clinical");
  const PROGRAMS = usePrograms();
  const [notes, setNotes] = useState([]);
  const [signedFilter, setSignedFilter] = useState("");
  const [noteForm, setNoteForm] = useState(NOTE_EMPTY);
  const [noteModal, setNoteModal] = useState(false);
  const [editNoteId, setEditNoteId] = useState(null);
  const [viewNote, setViewNote] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [signingNote, setSigningNote] = useState(null);

  const load = () => {
    const params = { category: CATEGORY };
    if (signedFilter) params.signed = signedFilter;
    api.get(`/clients/${client.id}/chart-notes`, { params })
      .then((r) => setNotes(r.data)).catch((e) => toast.error(apiError(e)));
  };
  useEffect(() => { if (client?.id) load(); }, [client?.id, signedFilter]); // eslint-disable-line

  const providerDefault = [user?.first_name, user?.last_name].filter(Boolean).join(" ");

  const toggleSign = async (n) => { setSigningNote(n); };
  const applySignature = async ({ image, name }) => {
    const n = signingNote;
    if (!n) return;
    try {
      await api.put(`/chart-notes/${n.id}`, { signed: true, signature_name: name || "", signature_image: image || "" });
      toast.success("Note signed and locked with audit stamp");
      setSigningNote(null); load();
    } catch (err) { toast.error(apiError(err)); }
  };
  const saveNote = async (e) => {
    e.preventDefault();
    if (!noteForm.note_type.trim()) return toast.error("Note type is required");
    try {
      if (editNoteId) await api.put(`/chart-notes/${editNoteId}`, noteForm);
      else await api.post("/chart-notes", { ...noteForm, client_id: client.id, category: CATEGORY });
      toast.success(editNoteId ? "Medical note updated" : "Medical note added");
      setNoteModal(false); setEditNoteId(null); setNoteForm(NOTE_EMPTY); load();
    } catch (err) { toast.error(apiError(err)); }
  };
  const removeNote = async () => {
    try {
      await api.delete(`/chart-notes/${confirmDelete.id}`);
      toast.success("Medical note removed");
      setConfirmDelete(null); load();
    } catch (err) { toast.error(apiError(err)); }
  };
  const openAdd = () => {
    setNoteForm({ ...NOTE_EMPTY, provider: providerDefault });
    setEditNoteId(null); setNoteModal(true);
  };
  const openEdit = (n) => {
    if (n.locked) { return toast.error("This note is locked. Ask an admin to unlock, or amend it."); }
    setNoteForm({ date: n.date, time: n.time, note_type: n.note_type,
      provider: n.provider, program: n.program, category: CATEGORY, custom_notes: n.custom_notes || "" });
    setEditNoteId(n.id); setNoteModal(true);
  };

  return (
    <div data-testid="medical-notes-panel">
      <Card data-testid="medical-notes-card">
        <div className="flex flex-wrap items-center justify-between gap-3 p-5 pb-3">
          <div>
            <h3 className="font-heading font-bold text-[#3E2723]">All Medical Notes</h3>
            <div className="text-[11px] text-[#7B6157]">Provider notes filed under the Medical chart category.</div>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1.5 rounded-lg border border-[#E5DFD3] pl-3 pr-1 h-10">
              <Filter size={14} className="text-[#7B6157] shrink-0" />
              <NativeLikeSelect data-testid="medical-notes-signed-filter" value={signedFilter}
                onChange={(e) => setSignedFilter(e.target.value)}
                triggerClass="h-8 w-36 border-0 bg-transparent px-1 text-sm text-[#4E342E] focus:ring-0">
                <option value="">Filter by: All</option>
                <option value="true">Signed</option>
                <option value="false">Unsigned</option>
              </NativeLikeSelect>
            </div>
            {editable && (
              <Btn data-testid="medical-notes-add-btn" variant="outline" className="rounded-lg" onClick={openAdd}>
                <Plus size={14} /> Add Medical Note
              </Btn>
            )}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead><tr>{["", "Status", "Date", "Time", "Note Type", "Provider", "Program", "Lock / Amend", ""].map((h) => (
              <th key={h} className="bg-[#FDFCF8] border-y border-[#EFEBE0] text-[#4E342E] font-semibold py-3 px-4 whitespace-nowrap">{h}</th>))}</tr></thead>
            <tbody>
              {notes.map((n) => (
                <VersionAccordion key={n.id} record={n} kind="chart_note" colSpan={CN_COLS}
                  primaryTestId={`medical-note-${n.id}`} rowClassName="hover:bg-[#FDFCF8]"
                  renderPrimary={({ expandToggle, versionBadge }) => (
                    <>
                      <td className="border-b border-[#EFEBE0] py-3 px-4 w-8">{expandToggle}</td>
                      <td className="border-b border-[#EFEBE0] py-3 px-4">
                        {n.signed ? (
                          <span data-testid={`medical-note-status-${n.id}`} className="inline-flex items-center gap-1 rounded-full bg-[#E8F5E9] px-2.5 py-1 text-[10px] font-bold text-[#2E7D32]"><Check size={11} /> Signed</span>
                        ) : (
                          <span data-testid={`medical-note-status-${n.id}`} className="inline-flex items-center gap-1 rounded-full bg-[#FFF3E0] px-2.5 py-1 text-[10px] font-bold text-[#8D5A00]">Draft</span>
                        )}
                        {versionBadge && <span className="ml-1.5">{versionBadge}</span>}
                      </td>
                      <td className="border-b border-[#EFEBE0] py-3 px-4 tabular-nums">{dayjs(n.date).format("MM/DD/YYYY")}</td>
                      <td className="border-b border-[#EFEBE0] py-3 px-4 tabular-nums">{n.time}</td>
                      <td className="border-b border-[#EFEBE0] py-3 px-4">{n.note_type}</td>
                      <td className="border-b border-[#EFEBE0] py-3 px-4">{n.provider}</td>
                      <td className="border-b border-[#EFEBE0] py-3 px-4">{n.program}</td>
                      <td className="border-b border-[#EFEBE0] py-3 px-4">
                        <div data-testid={`medical-note-lock-controls-${n.id}`} className="flex flex-wrap items-center gap-1.5">
                          <ClinicalRecordLockControls kind="chart_note" testKind={`chart-note-${n.id}`}
                            record={n} onChange={load}
                            endpointPathBuilder={(id) => `/chart-notes/${id}`}
                            amendableFields={CHART_NOTE_AMEND_FIELDS} />
                        </div>
                      </td>
                      <td className="border-b border-[#EFEBE0] py-3 px-4">
                        <div className="flex gap-2 justify-end">
                          <button type="button" data-testid={`medical-note-view-${n.id}`} aria-label="View" onClick={() => setViewNote(n)}
                            className="h-8 w-8 rounded-full bg-[#F5F2EB] text-[#7B6157] flex items-center justify-center hover:bg-[#EDE7DC]"><Eye size={13} /></button>
                          {editable && !n.signed && !n.locked && (
                            <button type="button" data-testid={`medical-note-sign-${n.id}`} aria-label="Sign" title="Sign note" onClick={() => toggleSign(n)}
                              className="h-8 w-8 rounded-full bg-[#F5F2EB] text-[#2E7D32] flex items-center justify-center hover:bg-[#E8F5E9]"><Check size={13} /></button>
                          )}
                          {editable && !n.locked && (
                            <button type="button" data-testid={`medical-note-edit-${n.id}`} aria-label="Edit" onClick={() => openEdit(n)}
                              className="h-8 w-8 rounded-full bg-[#F5F2EB] text-[#7B6157] flex items-center justify-center hover:bg-[#EDE7DC]"><Pencil size={13} /></button>
                          )}
                          {editable && !n.locked && (
                            <button type="button" data-testid={`medical-note-delete-${n.id}`} aria-label="Delete" onClick={() => setConfirmDelete(n)}
                              className="h-8 w-8 rounded-full bg-[#F5F2EB] text-[#7B6157] flex items-center justify-center hover:bg-[#FFEBEE] hover:text-[#C62828]"><Trash2 size={13} /></button>
                          )}
                        </div>
                      </td>
                    </>
                  )}
                />
              ))}
            </tbody>
          </table>
          {notes.length === 0 && <Empty text="No medical notes for this client" />}
        </div>
      </Card>

      <SlidePanel open={noteModal} onClose={() => setNoteModal(false)}
        title={editNoteId ? "Edit Medical Note" : "Add Medical Note"}
        subtitle={client ? `${client.first_name} ${client.last_name} - medical chart` : ""}
        testId="medical-note-slide-panel">
        <form onSubmit={saveNote} className="space-y-4">
          <div>
            <FLabel>Note Type</FLabel>
            <SelectWithCustom testId="medical-note-type-input" value={noteForm.note_type}
              onChange={(v) => setNoteForm({ ...noteForm, note_type: v })}
              options={NOTE_TYPES} customPlaceholder="Enter custom note type" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><FLabel>Date</FLabel><DateField testId="medical-note-date-input" value={noteForm.date}
              onChange={(v) => setNoteForm({ ...noteForm, date: v })} /></div>
            <div><FLabel>Time</FLabel><FInput type="time" value={noteForm.time}
              onChange={(e) => setNoteForm({ ...noteForm, time: e.target.value })} /></div>
          </div>
          <div><FLabel>Provider</FLabel><FInput data-testid="medical-note-provider-input" value={noteForm.provider}
            onChange={(e) => setNoteForm({ ...noteForm, provider: e.target.value })} /></div>
          <div><FLabel>Program</FLabel><SelectWithCustom testId="medical-note-program-input" value={noteForm.program}
            onChange={(v) => setNoteForm({ ...noteForm, program: v })}
            options={PROGRAMS} customPlaceholder="Enter program name" /></div>
          <div><FLabel>Custom Notes</FLabel>
            <textarea data-testid="medical-note-custom-notes-input" rows={4} value={noteForm.custom_notes || ""}
              onChange={(e) => setNoteForm({ ...noteForm, custom_notes: e.target.value })}
              placeholder="Free text notes, observations, or context."
              className="w-full rounded-lg border border-[#E5DFD3] bg-white px-3 py-2 text-sm text-[#3E2723] outline-none focus:border-[#8D6E63]" />
          </div>
          <div className="flex justify-end gap-2 border-t border-[#F1EEE7] pt-4">
            <Btn type="button" variant="ghost" onClick={() => setNoteModal(false)}>Cancel</Btn>
            <Btn data-testid="medical-note-save-btn" type="submit">{editNoteId ? "Save" : "Add"}</Btn>
          </div>
        </form>
      </SlidePanel>

      <SlidePanel open={!!viewNote} onClose={() => setViewNote(null)} title="Medical Note" testId="medical-note-view-panel">
        {viewNote && (
          <div className="space-y-2 text-sm">
            <div><span className="text-[#7B6157]">Date: </span><span className="font-semibold text-[#3E2723]">{dayjs(viewNote.date).format("MMM D, YYYY")} {viewNote.time}</span></div>
            <div><span className="text-[#7B6157]">Note Type: </span><span className="font-semibold text-[#3E2723]">{viewNote.note_type}</span></div>
            <div><span className="text-[#7B6157]">Provider: </span><span className="font-semibold text-[#3E2723]">{viewNote.provider || "-"}</span></div>
            <div><span className="text-[#7B6157]">Program: </span><span className="font-semibold text-[#3E2723]">{viewNote.program || "-"}</span></div>
            <div><span className="text-[#7B6157]">Signed: </span><span className="font-semibold">{viewNote.signed ? "Yes" : "No"}</span></div>
            {viewNote.custom_notes && (
              <div className="mt-2">
                <div className="text-[11px] font-semibold uppercase tracking-wide text-[#7B6157]">Custom Notes</div>
                <p data-testid="medical-note-view-custom-notes" className="mt-1 whitespace-pre-wrap rounded-lg bg-[#FDFCF8] border border-[#EFEBE0] p-3 text-sm text-[#3E2723]">{viewNote.custom_notes}</p>
              </div>
            )}
            {viewNote.amended_by_name && (
              <div className="mt-2 rounded-lg bg-[#FFF3E0] p-2 text-xs text-[#8D5A00]">
                Amended by {viewNote.amended_by_name} on {dayjs(viewNote.amended_at).format("MMM D, YYYY h:mm A")}: {viewNote.last_change_summary}
              </div>
            )}
          </div>
        )}
      </SlidePanel>

      <ConfirmDialog open={!!confirmDelete} onCancel={() => setConfirmDelete(null)} onConfirm={removeNote}
        title="Delete medical note"
        description={confirmDelete ? `Delete the ${confirmDelete.note_type || "medical"} note dated ${dayjs(confirmDelete.date).format("MM/DD/YYYY")}?` : ""} />

      <SignaturePromptModal open={!!signingNote} onClose={() => setSigningNote(null)}
        onConfirm={applySignature}
        title="Sign medical note"
        subtitle={signingNote ? `Signing ${signingNote.note_type || "medical note"} dated ${dayjs(signingNote.date).format("MMM D, YYYY")}. Locking is HIPAA-compliant with a full audit stamp.` : ""} />
    </div>
  );
}
